import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, CalendarCheck, FileText } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { inputCls, Field } from "@/components/site/ServiceChips";
import { API, SLOT_LABELS, formatApiError, serviceTitle } from "@/lib/content";

const http = axios.create({ baseURL: API, withCredentials: true });
const STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const TONE = { pending: "text-amber-300", confirmed: "text-emerald-300", completed: "text-sky-300", cancelled: "text-slate-500" };

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      onDone(data);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="mx-auto mt-24 max-w-sm space-y-5 rounded-3xl border border-white/10 bg-[#111827] p-8" data-testid="admin-login-form">
      <h1 className="font-display text-3xl font-bold text-white">Owner login</h1>
      <Field label="Email"><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="admin-email-input" /></Field>
      <Field label="Password"><input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="admin-password-input" /></Field>
      {err && <p className="text-sm text-red-400" data-testid="admin-login-error">{err}</p>}
      <button disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 py-3 font-semibold text-[#0B0F17] hover:bg-amber-300" data-testid="admin-login-submit">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
      </button>
    </form>
  );
};

const Item = ({ kind, it, onStatus }) => (
  <div className="grid gap-4 rounded-2xl border border-white/10 bg-[#111827] p-5 md:grid-cols-[1fr_auto]" data-testid={`admin-item-${it.id}`}>
    <div className="min-w-0 space-y-1.5">
      <p className="font-display text-lg font-bold text-white">{it.name} <span className="ml-2 font-mono-x text-xs font-normal text-slate-400">{new Date(it.created_at).toLocaleString()}</span></p>
      <p className="text-sm text-slate-300">{it.email} · {it.phone}</p>
      {kind === "bookings" ? (
        <p className="text-sm text-amber-200">{it.date} at {SLOT_LABELS[it.time] ?? it.time} — {it.address}</p>
      ) : (
        <p className="text-sm text-amber-200">{it.property_type} · {it.size || "size n/a"} · {it.frequency}</p>
      )}
      <p className="text-sm text-slate-300">{(it.services ?? []).map(serviceTitle).join(", ")}</p>
      {it.promo_code && <p className="inline-block rounded-full bg-emerald-400/10 px-3 py-0.5 font-mono-x text-xs text-emerald-300">Promo: {it.promo_code}</p>}
      {it.notes && <p className="text-sm italic text-slate-400">"{it.notes}"</p>}
    </div>
    <select value={it.status} onChange={(e) => onStatus(it.id, e.target.value)} data-testid={`admin-status-${it.id}`} className={`h-10 rounded-full border border-white/15 bg-[#0B0F17] px-4 text-sm capitalize ${TONE[it.status] ?? ""}`}>
      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  </div>
);

const Dashboard = ({ user, onLogout }) => {
  const [kind, setKind] = useState("bookings");
  const [items, setItems] = useState(null);
  const load = useCallback(async () => {
    setItems(null);
    try {
      const { data } = await http.get(`/admin/${kind}`);
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
      toast.error("Could not load data");
    }
  }, [kind]);
  useEffect(() => { load(); }, [load]);
  const onStatus = async (id, status) => {
    try {
      await http.patch(`/admin/${kind}/${id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Marked ${status}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  return (
    <div className="mx-auto max-w-5xl px-5 py-10">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div className="inline-flex rounded-full border border-white/10 bg-white/5 p-1">
          {[["bookings", "Bookings", CalendarCheck], ["quotes", "Quote requests", FileText]].map(([id, label, Icon]) => (
            <button key={id} onClick={() => setKind(id)} data-testid={`admin-tab-${id}`} className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${kind === id ? "bg-amber-400 text-[#0B0F17]" : "text-slate-300"}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
        <button onClick={onLogout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-sm text-slate-300 hover:text-amber-300"><LogOut className="h-4 w-4" /> {user.email}</button>
      </div>
      {items === null ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-amber-300" /> : items.length === 0 ? (
        <p className="py-16 text-center text-slate-400" data-testid="admin-empty">Nothing here yet.</p>
      ) : (
        <div className="space-y-3" data-testid="admin-list">{items.map((it) => <Item key={it.id} kind={kind} it={it} onStatus={onStatus} />)}</div>
      )}
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  const logout = async () => {
    await http.post("/auth/logout").catch(() => {});
    setUser(false);
  };
  return (
    <main className="min-h-screen" data-testid="admin-page">
      <header className="border-b border-white/10"><div className="mx-auto flex h-[72px] max-w-5xl items-center px-5"><Logo /></div></header>
      {user === null ? <Loader2 className="mx-auto mt-24 h-6 w-6 animate-spin text-amber-300" /> : user ? <Dashboard user={user} onLogout={logout} /> : <Login onDone={setUser} />}
    </main>
  );
}
